import React, { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

import Navbar from 'components/layout/NavbarNew';
import UserMenu from 'components/layout/UserMenu';
import PendencyCard, { PendencyCardLink } from 'design-system/PendencyCard';
import Skeleton from 'design-system/Skeleton';
import { pushGtmEvent } from 'analytics/gtm';
import { useCustomerStatusOnEnter } from 'hooks/useCustomerStatus';
import { REASON_TAG_LABEL, hasReason } from 'utils/blockReasons';
import type { BlockReasonId } from 'utils/blockReasons';
import { buildWhatsAppHref } from 'utils/buildContactLinks';
import {
  ConsultantDataReponse,
  getConsultants,
} from 'services/consultantsServices';

import { PENDENCIAS_TAG_CATEGORY, PENDENCIAS_TEXTS } from './constants';
import { ContactCardIcon, ReceiptIcon, WrenchSettingsIcon } from './icons';
import CallCenterModal from './modals/CallCenterModal';
import CompensationModal from './modals/CompensationModal';
import ConsultantModal from './modals/ConsultantModal';
import IrregularBasesModal from './modals/IrregularBasesModal';
import OverdueSlipsModal from './modals/OverdueSlipsModal';
import { useIrregularBases } from './useIrregularBases';
import { CardsPanel, Container, Header, Subtitle, Title } from './styles';

type ModalName =
  | 'call-center'
  | 'compensation'
  | 'consultant'
  | 'slips'
  | 'bases'
  | null;

const trackClick = (label: string): void => {
  pushGtmEvent({
    event: 'click_cta',
    eventCategory: PENDENCIAS_TAG_CATEGORY,
    eventAction: 'click',
    eventLabel: label,
    elementType: 'button',
  });
};

const trackView = (label: string): void => {
  pushGtmEvent({
    event: 'view_content',
    eventCategory: PENDENCIAS_TAG_CATEGORY,
    eventAction: 'view',
    eventLabel: label,
    elementType: 'message',
  });
};

const Pendencias = (): JSX.Element => {
  const history = useHistory();
  const { reasons, flagsByBase, isLoading } = useCustomerStatusOnEnter();
  const { bases: irregularBases, isLoading: isLoadingBases } =
    useIrregularBases(flagsByBase);

  const [openModal, setOpenModal] = useState<ModalName>(null);
  const [consultant, setConsultant] = useState<ConsultantDataReponse | null>(
    null
  );
  const viewedCards = useRef<Set<string>>(new Set());
  const pageViewSent = useRef(false);

  const show = (id: BlockReasonId): boolean => hasReason(reasons, id);

  useEffect(() => {
    if (!show('IRREGULAR_TAXPAYER') || consultant) return;

    const load = async (): Promise<void> => {
      const response = await getConsultants();
      setConsultant(response?.[0] ?? null);
    };

    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reasons]);

  useEffect(() => {
    if (isLoading || reasons.length > 0) return;
    history.replace('/portal');
  }, [history, isLoading, reasons]);

  useEffect(() => {
    if (isLoading || pageViewSent.current) return;

    pageViewSent.current = true;
    trackView('tela de pendencias');
  }, [isLoading]);

  useEffect(() => {
    reasons.forEach((reason) => {
      const label = REASON_TAG_LABEL[reason.id];
      if (viewedCards.current.has(label)) return;

      viewedCards.current.add(label);
      trackView(label);
    });
  }, [reasons]);

  const openWhatsApp = (): void => {
    trackClick(PENDENCIAS_TEXTS.TAXPAYER_ACTION_CONSULTANT_CHAT);

    const href = consultant?.phone ? buildWhatsAppHref(consultant.phone) : '';
    if (href) {
      window.open(href, '_blank', 'noopener');
      return;
    }

    setOpenModal('call-center');
  };

  const openCallCenter = (): void => {
    trackClick(PENDENCIAS_TEXTS.ACTION_CALL_CENTER);
    setOpenModal('call-center');
  };

  const overdueDescription = (
    <>
      {PENDENCIAS_TEXTS.OVERDUE_DESCRIPTION}
      <PendencyCardLink
        onClick={() => {
          trackClick(PENDENCIAS_TEXTS.OVERDUE_LINK);
          setOpenModal('compensation');
        }}
      >
        {PENDENCIAS_TEXTS.OVERDUE_LINK}
      </PendencyCardLink>
    </>
  );

  const baseDescription = (
    <>
      {PENDENCIAS_TEXTS.BASE_DESCRIPTION_PREFIX}
      <strong>{PENDENCIAS_TEXTS.BASE_DESCRIPTION_STRONG}</strong>
      {PENDENCIAS_TEXTS.BASE_DESCRIPTION_SUFFIX}
    </>
  );

  const content = (): React.ReactNode => {
    if (isLoading) {
      return (
        <CardsPanel>
          <Skeleton height={152} label="Verificando a situação do cliente" />
          <Skeleton height={133} />
        </CardsPanel>
      );
    }

    if (reasons.length === 0) return null;

    return (
      <CardsPanel>
        {show('OVERDUE_PAYMENT') && (
          <PendencyCard
            icon={<ReceiptIcon />}
            title={PENDENCIAS_TEXTS.OVERDUE_TITLE}
            description={overdueDescription}
            actions={[
              {
                label: PENDENCIAS_TEXTS.OVERDUE_ACTION_SLIPS,
                variant: 'secondary',
                onClick: () => {
                  trackClick(PENDENCIAS_TEXTS.OVERDUE_ACTION_SLIPS);
                  setOpenModal('slips');
                },
              },
              {
                label: PENDENCIAS_TEXTS.ACTION_CALL_CENTER,
                variant: 'primary',
                onClick: openCallCenter,
              },
            ]}
          />
        )}

        {show('PAYMENT_TERM') && (
          <PendencyCard
            icon={<ReceiptIcon />}
            title={PENDENCIAS_TEXTS.PAYMENT_TERM_TITLE}
            description={PENDENCIAS_TEXTS.PAYMENT_TERM_DESCRIPTION}
            actions={[
              {
                label: PENDENCIAS_TEXTS.ACTION_CALL_CENTER,
                variant: 'primary',
                onClick: openCallCenter,
              },
            ]}
          />
        )}

        {show('IRREGULAR_TAXPAYER') && (
          <PendencyCard
            icon={<ContactCardIcon />}
            title={PENDENCIAS_TEXTS.TAXPAYER_TITLE}
            description={PENDENCIAS_TEXTS.TAXPAYER_DESCRIPTION}
            actions={[
              {
                label: PENDENCIAS_TEXTS.TAXPAYER_ACTION_CONSULTANT_DATA,
                variant: 'secondary',
                onClick: () => {
                  trackClick(PENDENCIAS_TEXTS.TAXPAYER_ACTION_CONSULTANT_DATA);
                  setOpenModal('consultant');
                },
              },
              {
                label: PENDENCIAS_TEXTS.TAXPAYER_ACTION_CONSULTANT_CHAT,
                variant: 'primary',
                onClick: openWhatsApp,
              },
            ]}
          />
        )}

        {show('IRREGULAR_BASE') && (
          <PendencyCard
            icon={<WrenchSettingsIcon />}
            title={PENDENCIAS_TEXTS.BASE_TITLE}
            description={baseDescription}
            actions={[
              {
                label: PENDENCIAS_TEXTS.BASE_ACTION_LIST,
                variant: 'secondary',
                onClick: () => {
                  trackClick(PENDENCIAS_TEXTS.BASE_ACTION_LIST);
                  setOpenModal('bases');
                },
              },
              {
                label: PENDENCIAS_TEXTS.ACTION_CALL_CENTER,
                variant: 'primary',
                onClick: openCallCenter,
              },
            ]}
          />
        )}
      </CardsPanel>
    );
  };

  return (
    <Navbar selected="pendencias" openSubMenu="abastecimento">
      <UserMenu intro={() => true} />
      <Container>
        <Header>
          <Title>{PENDENCIAS_TEXTS.PAGE_TITLE}</Title>
          <Subtitle>{PENDENCIAS_TEXTS.PAGE_SUBTITLE}</Subtitle>
        </Header>

        {content()}
      </Container>

      <CallCenterModal
        open={openModal === 'call-center'}
        onClose={() => setOpenModal(null)}
      />
      <CompensationModal
        open={openModal === 'compensation'}
        onClose={() => setOpenModal(null)}
      />
      <ConsultantModal
        open={openModal === 'consultant'}
        onClose={() => setOpenModal(null)}
      />
      <OverdueSlipsModal
        open={openModal === 'slips'}
        onClose={() => setOpenModal(null)}
      />
      <IrregularBasesModal
        open={openModal === 'bases'}
        bases={irregularBases}
        isLoading={isLoadingBases}
        onClose={() => setOpenModal(null)}
        onCallCenter={openCallCenter}
      />
    </Navbar>
  );
};

export default Pendencias;
